/**
 * Critical CSS Loader and Resource Optimization
 * Handles dynamic loading of non-critical CSS and resource management
 */

(function() {
  'use strict';

  // Critical CSS Loader Class
  class CriticalCSSLoader {
    constructor() {
      this.loadedStyles = new Set();
      this.pendingLoads = new Map();
      this.init();
    }

    init() {
      // Load critical resources immediately
      this.loadCriticalResources();
      
      // Set up intersection observer for lazy loading
      this.setupIntersectionObserver();
      
      // Load non-critical resources after page load
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.loadNonCriticalResources();
        });
      } else {
        this.loadNonCriticalResources();
      }
    }

    loadCriticalResources() {
      // Preload critical JavaScript files
      const criticalScripts = [
        window.Shopify?.routes?.cart_add_url ? '/assets/cart.js' : null
      ].filter(Boolean);

      criticalScripts.forEach(src => {
        this.preloadScript(src);
      });
    }

    loadNonCriticalResources() {
      // Get base asset URL from Shopify
      const assetBaseUrl = window.Shopify?.routes?.root || '';
      const themeId = window.Shopify?.theme?.id || '';
      
      // Build proper asset URLs
      const buildAssetUrl = (filename) => {
        // Try to get from existing link tags first
        const existingLink = document.querySelector(`link[href*="${filename}"]`);
        if (existingLink) {
          return existingLink.href;
        }
        // Fallback to standard Shopify asset path
        return `/cdn/shop/t/${themeId}/assets/${filename}`;
      };

      // Load non-critical CSS with proper fallbacks
      const nonCriticalCSS = [
        {
          href: buildAssetUrl('announcement-bar-positioning.css'),
          media: 'all',
          priority: 'high'
        },
        {
          href: buildAssetUrl('performance-optimizations.css'),
          media: 'all',
          priority: 'high'
        },
        {
          href: buildAssetUrl('value-proposition-strip.css'),
          media: 'all',
          priority: 'medium'
        },
        {
          href: buildAssetUrl('social-proof.css'),
          media: 'all',
          priority: 'low'
        },
        {
          href: buildAssetUrl('trust-quality.css'),
          media: 'all',
          priority: 'low'
        }
      ];

      // Load high priority CSS first
      const highPriority = nonCriticalCSS.filter(css => css.priority === 'high');
      const mediumPriority = nonCriticalCSS.filter(css => css.priority === 'medium');
      const lowPriority = nonCriticalCSS.filter(css => css.priority === 'low');

      // Stagger loading to prevent blocking
      this.loadCSSBatch(highPriority, 0);
      this.loadCSSBatch(mediumPriority, 100);
      this.loadCSSBatch(lowPriority, 300);
    }

    loadCSSBatch(cssBatch, delay) {
      setTimeout(() => {
        cssBatch.forEach(css => {
          this.loadCSS(css.href, css.media);
        });
      }, delay);
    }

    loadCSS(href, media = 'all') {
      return new Promise((resolve, reject) => {
        // Check if already loaded
        if (this.loadedStyles.has(href)) {
          resolve();
          return;
        }

        // Check if currently loading
        if (this.pendingLoads.has(href)) {
          this.pendingLoads.get(href).then(resolve).catch(reject);
          return;
        }

        // Create loading promise
        const loadPromise = new Promise((res, rej) => {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.media = media;

          const onLoad = () => {
            this.loadedStyles.add(href);
            this.pendingLoads.delete(href);
            res();
          };

          const onError = () => {
            this.pendingLoads.delete(href);
            rej(new Error(`Failed to load CSS: ${href}`));
          };

          if (link.addEventListener) {
            link.addEventListener('load', onLoad);
            link.addEventListener('error', onError);
          } else if (link.attachEvent) {
            link.attachEvent('onload', onLoad);
            link.attachEvent('onerror', onError);
          }

          document.head.appendChild(link);
        });

        this.pendingLoads.set(href, loadPromise);
        loadPromise.then(resolve).catch(reject);
      });
    }

    preloadScript(src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = src;
      document.head.appendChild(link);
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        // Fallback for browsers without IntersectionObserver
        this.loadAllImages();
        return;
      }

      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImage(entry.target);
            imageObserver.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // Observe all lazy images
      document.querySelectorAll('img[data-src], img[data-srcset]').forEach(img => {
        imageObserver.observe(img);
      });
    }

    loadImage(img) {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
      img.classList.remove('lazy');
      
      // Add loaded class for styling
      img.classList.add('loaded');
    }

    loadAllImages() {
      // Fallback: load all images immediately
      document.querySelectorAll('img[data-src], img[data-srcset]').forEach(img => {
        this.loadImage(img);
      });
    }

    // Public method to load CSS on demand
    static loadOnDemand(href, media = 'all') {
      if (!window.criticalCSSLoader) {
        window.criticalCSSLoader = new CriticalCSSLoader();
      }
      return window.criticalCSSLoader.loadCSS(href, media);
    }
  }

  // Layout Shift Prevention
  class LayoutShiftPrevention {
    constructor() {
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.preventLayoutShifts();
        });
      } else {
        this.preventLayoutShifts();
      }

      // Also run on dynamic content changes
      this.observeContentChanges();
    }

    preventLayoutShifts() {
      // Set aspect ratios for images without dimensions
      const images = document.querySelectorAll('img:not([width]):not([height]):not(.header__heading-logo):not([class*="logo"]):not([alt*="logo"]):not([src*="logo"]):not(.slideshow__image img):not(.slideshow__media img):not(.banner__media img)');
      
      images.forEach(img => {
        if (!img.style.aspectRatio && !img.closest('.slideshow')) {
          // Use natural aspect ratio if available, otherwise default to square
          if (img.naturalWidth && img.naturalHeight) {
            img.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
          } else {
            img.style.aspectRatio = '1 / 1';
          }
          img.style.objectFit = 'cover';
        }
      });

      // Prevent text layout shifts
      this.preventTextLayoutShifts();
    }

    preventTextLayoutShifts() {
      // Add font-display: swap to prevent invisible text
      const style = document.createElement('style');
      style.textContent = `
        @font-face {
          font-display: swap;
        }
      `;
      document.head.appendChild(style);
    }

    observeContentChanges() {
      if ('MutationObserver' in window) {
        const observer = new MutationObserver((mutations) => {
          let shouldPreventShifts = false;
          
          mutations.forEach(mutation => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              mutation.addedNodes.forEach(node => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  if (node.tagName === 'IMG' || node.querySelector('img')) {
                    shouldPreventShifts = true;
                  }
                }
              });
            }
          });

          if (shouldPreventShifts) {
            setTimeout(() => this.preventLayoutShifts(), 0);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      }
    }
  }

  // Performance Monitoring
  class PerformanceMonitor {
    constructor() {
      this.metrics = {};
      this.init();
    }

    init() {
      if ('PerformanceObserver' in window) {
        this.observeLayoutShifts();
        this.observeLargestContentfulPaint();
        this.observeFirstInputDelay();
      }
    }

    observeLayoutShifts() {
      try {
        const observer = new PerformanceObserver((list) => {
          let clsValue = 0;
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          }
          this.metrics.cls = (this.metrics.cls || 0) + clsValue;
        });
        observer.observe({ type: 'layout-shift', buffered: true });
      } catch (e) {
        // Silently fail if not supported
      }
    }

    observeLargestContentfulPaint() {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.metrics.lcp = lastEntry.startTime;
        });
        observer.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (e) {
        // Silently fail if not supported
      }
    }

    observeFirstInputDelay() {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.metrics.fid = entry.processingStart - entry.startTime;
          }
        });
        observer.observe({ type: 'first-input', buffered: true });
      } catch (e) {
        // Silently fail if not supported
      }
    }

    getMetrics() {
      return this.metrics;
    }
  }

  // Initialize all optimizations
  function initializeOptimizations() {
    // Initialize Critical CSS Loader
    window.criticalCSSLoader = new CriticalCSSLoader();
    
    // Initialize Layout Shift Prevention
    new LayoutShiftPrevention();
    
    // Initialize Performance Monitoring
    window.performanceMonitor = new PerformanceMonitor();
  }

  // Start optimizations
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeOptimizations);
  } else {
    initializeOptimizations();
  }

  // Export for external use
  window.CriticalCSSLoader = CriticalCSSLoader;
})();